/**
 * 나에게 맞는 공고 — 서버 응답을 카드 한 장에 필요한 값으로 바꾼다.
 *
 * 서버는 기존 공고/배역 카드 DTO에 rank·fitPercentile·fitMode·reason을 얹어 준다.
 * 공고와 배역이 post/role로 나뉘어 와도, 한 객체에 펼쳐져 와도 받는다.
 */

const GENDER_LABELS = { M: '남성', MALE: '남성', F: '여성', FEMALE: '여성' };

function pickList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.content)) return data.content;
  return [];
}

function toSkills(role) {
  if (Array.isArray(role.skills)) return role.skills.filter(Boolean);
  if (typeof role.requiredSkills === 'string') {
    return role.requiredSkills.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/** 성별·나이·키·스킬을 짧은 조각들로 — 조건이 없으면 빈 배열 */
export function formatConditions(role) {
  const parts = [];
  const gender = GENDER_LABELS[String(role.gender || '').toUpperCase()];
  if (gender) parts.push(gender);

  const { ageMin, ageMax, heightMin, heightMax } = role;
  if (ageMin != null && ageMax != null) parts.push(`${Math.min(ageMin, ageMax)}~${Math.max(ageMin, ageMax)}세`);
  else if (ageMin != null) parts.push(`${ageMin}세 이상`);
  else if (ageMax != null) parts.push(`${ageMax}세 이하`);

  if (heightMin != null && heightMax != null) parts.push(`${heightMin}~${heightMax}cm`);
  else if (heightMin != null) parts.push(`${heightMin}cm 이상`);
  else if (heightMax != null) parts.push(`${heightMax}cm 이하`);

  const skills = toSkills(role);
  if (skills.length > 0) {
    parts.push(skills.length > 2 ? `${skills.slice(0, 2).join('·')} 외 ${skills.length - 2}` : skills.join('·'));
  }
  return parts;
}

function parseDate(value) {
  if (!value) return null;
  const s = String(value);
  // 'YYYY-MM-DD'는 UTC 자정으로 읽히므로 로컬 날짜로 직접 만든다
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * 마감일 표시: { label: '10.31 마감', dday: 'D-5', urgent } — 없으면 상시 모집
 * @param {string|null} deadline
 * @param {Date} [now]
 */
export function formatDeadline(deadline, now = new Date()) {
  const d = parseDate(deadline);
  if (!d) return { label: '상시 모집', dday: null, urgent: false };
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((day - today) / 86400000);
  const dday = diff === 0 ? 'D-Day' : diff > 0 ? `D-${diff}` : null;
  return {
    label: `${d.getMonth() + 1}.${String(d.getDate()).padStart(2, '0')} 마감`,
    dday,
    urgent: diff >= 0 && diff <= 3,
  };
}

/**
 * @returns {Array<{ key: string, postId: string, roleId: string|null, title: string,
 *   roleName: string, roleType: string, projectType: string, conditions: string[],
 *   deadline: string|null, reason: string, rank: number|null }>}
 */
export function normalizePostingRecommendations(data) {
  return pickList(data)
    .map((item, index) => {
      if (!item) return null;
      const post = item.post || item.castingPost || item;
      const role = item.role || item.castingRole || item;
      const postId = post.id ?? item.postId ?? item.castingPostId ?? role.castingPostId;
      if (postId == null) return null;
      const roleId = item.role ? role.id : (item.roleId ?? null);
      return {
        key: `${postId}:${roleId ?? index}`,
        postId: String(postId),
        roleId: roleId != null ? String(roleId) : null,
        title: post.title || '',
        roleName: role.roleName || role.name || '',
        roleType: role.roleType || '',
        projectType: post.projectType || post.category || '',
        conditions: formatConditions(role),
        deadline: post.applicationDeadline ?? item.applicationDeadline ?? null,
        reason: item.reason || '',
        rank: item.rank ?? null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
}
